// SPDX-License-Identifier: LicenseRef-Proprietary

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  DEFAULT_INPUTS,
  REGIONS,
  DISEASES,
  AUDIENCES,
  validateCoverage,
  type VaccineInputs,
  type Scenario,
} from '../components/VaccineDashboard/lib/models';
import type { ParameterSource } from '../components/VaccineDashboard/lib/parameterIntegration';
import {
  PopulationMode,
  normalizePopulationMode,
  getRegionPopulation,
  getNationalPopulation,
} from '../data/regions';
import { getItem, setItem, logActivity, cleanupActivityLogs } from '../components/VaccineDashboard/lib/storage';
import type { VaccineOutputs } from '../components/VaccineDashboard/lib/models';
import type { ValidationResult } from '../lib/outputValidation';

export interface DashboardSettings {
  visibleKPIs: {
    icer: boolean;
    qalysGained: boolean;
    budgetImpact: boolean;
    bcr: boolean;
    lifeYearsGained: boolean;
    deathsAverted: boolean;
    casesAvoided: boolean;
    hospitalizationsAvoided: boolean;
    adverseEventCost: boolean;
    productivityCost: boolean;
  };
  showDetailsExpanded: boolean;
}

const DEFAULT_DASHBOARD_SETTINGS: DashboardSettings = {
  visibleKPIs: {
    icer: true,
    qalysGained: true,
    budgetImpact: true,
    bcr: true,
    lifeYearsGained: false,
    deathsAverted: false,
    casesAvoided: false,
    hospitalizationsAvoided: false,
    adverseEventCost: false,
    productivityCost: true, // Default to true as it's a key feature
  },
  showDetailsExpanded: false,
};

type GlobalInputsState = Omit<
  VaccineInputs,
  'population' | 'selectedRegions' | 'defaultCoveragePct' | 'coveragePctByRegion'
> & {
  populationMode: PopulationMode;
  nationalCoveragePct: number;
  nationalPopulation: number;
  vaccineName?: string;
};

interface RegionalState {
  selectedRegions: string[];
  selectedRegionId: string | null;
  coveragePctByRegion: Record<string, number>;
  defaultCoveragePct: number;
}

const clampValue = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const normalizeSelectedRegions = (regions: string[]): string[] => {
  if (!regions || regions.length === 0) {
    return ['finland'];
  }
  if (regions.includes('finland')) {
    return ['finland'];
  }
  return Array.from(new Set(regions));
};

const normalizeRegionalSelection = (regional: RegionalState): RegionalState => {
  const normalized = normalizeSelectedRegions(regional.selectedRegions);
  const nextSelected =
    regional.selectedRegionId && normalized.includes(regional.selectedRegionId)
      ? regional.selectedRegionId
      : normalized[0] ?? 'finland';

  return {
    ...regional,
    selectedRegions: normalized,
    selectedRegionId: nextSelected,
  };
};

const derivePopulation = (selectedRegions: string[], mode: PopulationMode): number => {
  if (selectedRegions.includes('finland')) {
    return getNationalPopulation(mode);
  }
  return selectedRegions.reduce(
    (total, regionKey) => total + getRegionPopulation(regionKey, mode),
    0
  );
};

const clampCoverageMap = (
  coverage: Record<string, number>,
  selectedRegions: string[],
  defaultCoverage: number,
  cap: number
): Record<string, number> => {
  const nextCoverage: Record<string, number> = {};
  Object.entries(coverage).forEach(([key, value]) => {
    nextCoverage[key] = clampValue(value ?? 0, 0, cap);
  });

  if (selectedRegions.includes('finland')) {
    nextCoverage.finland = cap;
  }

  selectedRegions.forEach((regionKey) => {
    if (regionKey === 'finland') return;
    if (nextCoverage[regionKey] === undefined) {
      nextCoverage[regionKey] = clampValue(defaultCoverage, 0, cap);
    }
  });

  return nextCoverage;
};

const deriveInputsFromState = (
  globalInputs: GlobalInputsState,
  regional: RegionalState
): VaccineInputs => {
  const selectedRegions = normalizeSelectedRegions(regional.selectedRegions);
  const population = selectedRegions.includes('finland')
    ? globalInputs.nationalPopulation
    : derivePopulation(selectedRegions, globalInputs.populationMode);
  const { populationMode, nationalCoveragePct, vaccineName, ...baseInputs } = globalInputs;
  const coveragePctByRegion = clampCoverageMap(
    regional.coveragePctByRegion,
    selectedRegions,
    regional.defaultCoveragePct,
    nationalCoveragePct
  );

  // Explicitly preserve timeHorizonYears to prevent it from being lost
  // This ensures the value is preserved even if it's missing from globalInputs
  const preservedTimeHorizon = (globalInputs as any).timeHorizonYears ?? baseInputs.timeHorizonYears ?? 1;

  return {
    ...baseInputs,
    timeHorizonYears: preservedTimeHorizon,
    selectedRegions,
    population,
    populationMode, // Include this so it persists in inputs
    vaccineName,
    defaultCoveragePct: regional.defaultCoveragePct,
    coveragePctByRegion,
  };
};

const convertInputsToState = (
  inputs: VaccineInputs
): {
  globalInputs: GlobalInputsState;
  regional: RegionalState;
} => {
  const {
    population,
    selectedRegions,
    defaultCoveragePct,
    coveragePctByRegion,
    ...rest
  } = inputs;

  const inferredNationalCoverage =
    coveragePctByRegion?.finland ?? defaultCoveragePct ?? 75;
  const initialPopulationMode = normalizePopulationMode((rest as any).populationMode);
  const canonicalPopulation = getNationalPopulation(initialPopulationMode);

  const normalizedRegions = normalizeSelectedRegions(selectedRegions);

  const globalInputs: GlobalInputsState = {
    ...rest,
    populationMode: initialPopulationMode,
    nationalCoveragePct: clampValue(inferredNationalCoverage, 0, 100),
    nationalPopulation: normalizedRegions.includes('finland')
      ? population
      : canonicalPopulation,
    vaccineName: rest.vaccineName,
    // Explicitly preserve timeHorizonYears to ensure it's not lost during conversion
    timeHorizonYears: rest.timeHorizonYears ?? 1,
  };

  const regional: RegionalState = {
    selectedRegions: normalizedRegions,
    selectedRegionId: normalizedRegions[0] ?? 'finland',
    coveragePctByRegion: coveragePctByRegion ? { ...coveragePctByRegion } : {},
    defaultCoveragePct: defaultCoveragePct ?? 75,
  };

  return { globalInputs, regional };
};

const buildStateFromInputs = (inputs: VaccineInputs) => {
  const { globalInputs, regional } = convertInputsToState(inputs);
  const normalizedRegional = normalizeRegionalSelection(regional);
  const derivedInputs = deriveInputsFromState(globalInputs, normalizedRegional);
  
  // Explicitly preserve timeHorizonYears from saved inputs - this is the source of truth
  // The explicit assignment in convertInputsToState ensures it's in globalInputs,
  // but we use inputs.timeHorizonYears directly to guarantee it's never lost
  const preservedTimeHorizon = inputs.timeHorizonYears ?? 1;
  
  return {
    inputs: {
      ...derivedInputs,
      timeHorizonYears: preservedTimeHorizon,
    },
    globalInputs: {
      ...globalInputs,
      timeHorizonYears: preservedTimeHorizon,
    },
    regional: normalizedRegional,
  };
};

export const __dashboardStoreInternals = {
  normalizeSelectedRegions,
  deriveInputsFromState,
  convertInputsToState,
};

export interface VaccineDashboardState {
  // State
  inputs: VaccineInputs;
  globalInputs: GlobalInputsState;
  regional: RegionalState;
  audience: string;
  disease: string;
  scenarios: Scenario[];
  isModelInputsCollapsed: boolean;
  activeTab: string;
  backendResults: VaccineOutputs | null;
  backendComputedAt: number | null;
  validationResults: {
    overall: ValidationResult;
    qaly: ValidationResult;
    budgetImpact: ValidationResult;
  } | null;
  activity: any[];
  dashboardSettings: DashboardSettings;
  isImporting: boolean;
  // Parameter source tracking for audit trail
  parameterSources: Record<string, ParameterSource>;
  
  // Actions
  patchInputs: (patch: Partial<VaccineInputs>) => void;
  setAudience: (value: string) => void;
  setDisease: (value: string) => void;
  setPopulationMode: (mode: PopulationMode) => void;
  setNationalCoveragePct: (value: number) => void;
  setNationalPopulation: (value: number) => void;
  setSelectedRegion: (value: string) => void;
  setIsModelInputsCollapsed: (value: boolean) => void;
  setActiveTab: (value: string) => void;
  addScenario: (scenario: Scenario) => void;
  setBackendResults: (results: VaccineOutputs, computedAt: number, validation?: {
    overall: ValidationResult;
    qaly: ValidationResult;
    budgetImpact: ValidationResult;
  }) => void;
  clearBackendResults: () => void;
  resetToDefaults: () => void;
  // Parameter source tracking actions
  trackParameterSource: (paramName: string, source: ParameterSource) => void;
  getParameterSource: (paramName: string) => ParameterSource | undefined;

  handleCoverageChange: (region: string, value: number) => void;
  handleRegionToggle: (regionKey: string) => void;
  handleRegionsChange: (regions: string[]) => void;
  setActivity: (activity: any[]) => void;
  setDashboardSettings: (settings: Partial<DashboardSettings>) => void;
  loadScenario: (scenario: Scenario) => void;
  deleteScenario: (id: string) => void;
  attachEvidenceToScenario: (scenarioId: string, pmid: string) => void;
  detachEvidenceFromScenario: (scenarioId: string, pmid: string) => void;
}

// Debounced save function
let saveTimeout: ReturnType<typeof setTimeout> | null = null;
const debouncedSave = (state: VaccineDashboardState) => {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }
  saveTimeout = setTimeout(() => {
    try {
      const { inputs, audience, disease, scenarios, activeTab, dashboardSettings, parameterSources } = state;
      setItem('vaccineClient.inputs', inputs);
      setItem('vaccineClient.audience', audience);
      setItem('vaccineClient.disease', disease);
      setItem('vaccineClient.scenarios', scenarios);
      setItem('vaccineClient.activeTab', activeTab);
      setItem('vaccineClient.dashboardSettings', dashboardSettings);
      setItem('vaccineClient.parameterSources', parameterSources);
    } catch (error) {
      console.warn('Failed to save vaccine dashboard state:', error);
    }
  }, 500);
};

// Load from localStorage
export const loadFromStorage = (): Partial<VaccineDashboardState> => {
  try {
    cleanupActivityLogs();
    
    const savedInputs = getItem('vaccineClient.inputs', DEFAULT_INPUTS);
    // Migration: Reset waningPctPerYear to 0 if it's the old default of 10 or undefined
    // This feature is disabled and should default to 0 for backward compatibility
    if (savedInputs && (savedInputs.waningPctPerYear === 10 || savedInputs.waningPctPerYear === undefined)) {
      savedInputs.waningPctPerYear = 0;
    }
    
    // Migration: Accept both snake_case and camelCase keys, normalize to camelCase
    let needsMigration = false;
    if (savedInputs) {
      // Migrate booster_years → boosterYears
      if (savedInputs.boosterYears === undefined && (savedInputs as any).booster_years !== undefined) {
        savedInputs.boosterYears = (savedInputs as any).booster_years;
        delete (savedInputs as any).booster_years;
        needsMigration = true;
      }
      // Migrate booster_cost → boosterCost
      if (savedInputs.boosterCost === undefined && (savedInputs as any).booster_cost !== undefined) {
        savedInputs.boosterCost = (savedInputs as any).booster_cost;
        delete (savedInputs as any).booster_cost;
        needsMigration = true;
      }
      // Save migrated data back to localStorage (only camelCase going forward)
      if (needsMigration || (savedInputs.waningPctPerYear === 10 || savedInputs.waningPctPerYear === undefined)) {
        setItem('vaccineClient.inputs', savedInputs);
      }
    }
    const savedDisease = getItem('vaccineClient.disease', '');
    const savedScenarios = getItem('vaccineClient.scenarios', [] as Scenario[]);
    // Migration: Migrate scenarios array - accept both keys, normalize to camelCase
    let scenariosNeedsMigration = false;
    if (savedScenarios && savedScenarios.length > 0) {
      savedScenarios.forEach((scenario) => {
        // Normalize evidenceIds: ensure it always exists as []
        if (!scenario.evidenceIds) {
          scenario.evidenceIds = [];
          scenariosNeedsMigration = true;
        }
        if (scenario.inputs) {
          const inputs = scenario.inputs as any;
          if (inputs.boosterYears === undefined && inputs.booster_years !== undefined) {
            inputs.boosterYears = inputs.booster_years;
            delete inputs.booster_years;
            scenariosNeedsMigration = true;
          }
          if (inputs.boosterCost === undefined && inputs.booster_cost !== undefined) {
            inputs.boosterCost = inputs.booster_cost;
            delete inputs.booster_cost;
            scenariosNeedsMigration = true;
          }
        }
      });
      if (scenariosNeedsMigration) {
        setItem('vaccineClient.scenarios', savedScenarios);
      }
    }
    const savedActiveTab = getItem('vaccineClient.activeTab', 'composition');
    const savedActivity = getItem('vaccineClient.activity', []);
    const savedDashboardSettings = getItem('vaccineClient.dashboardSettings', DEFAULT_DASHBOARD_SETTINGS);
    const savedParameterSources = getItem('vaccineClient.parameterSources', {});
    
    return {
      inputs: savedInputs,
      audience: "", // Force empty audience
      disease: savedDisease,
      scenarios: savedScenarios,
      activeTab: savedActiveTab,
      activity: savedActivity,
      dashboardSettings: savedDashboardSettings,
      parameterSources: savedParameterSources,
    };
  } catch (error) {
    console.warn('Failed to load vaccine dashboard state:', error);
    return {
      inputs: DEFAULT_INPUTS,
      audience: AUDIENCES[0].key,
      disease: '',
      scenarios: [],
      activeTab: 'composition',
    };
  }
};

export const useVaccineDashboardStore = create<VaccineDashboardState>()(
  subscribeWithSelector((set, get) => {
    const loadedState = loadFromStorage();
    const baseInputs = loadedState.inputs || DEFAULT_INPUTS;
    const {
      globalInputs: initialGlobalInputs,
      regional: initialRegionalState,
    } = convertInputsToState(baseInputs);
    const normalizedRegional = normalizeRegionalSelection(initialRegionalState);
    const derivedInputs = deriveInputsFromState(initialGlobalInputs, normalizedRegional);
    
    return {
      // Initial state
      inputs: derivedInputs,
      globalInputs: initialGlobalInputs,
      regional: normalizedRegional,
      audience: "", // Default to empty string per user request
      disease: loadedState.disease || '',
      scenarios: loadedState.scenarios || [],
      isModelInputsCollapsed: true,
      activeTab: loadedState.activeTab || 'composition',
      backendResults: null,
      backendComputedAt: null,
      validationResults: null,
      activity: loadedState.activity ||[],
      dashboardSettings: loadedState.dashboardSettings || DEFAULT_DASHBOARD_SETTINGS,
      isImporting: false,
      parameterSources: loadedState.parameterSources || {},
      
      // Actions
      patchInputs: (patch: Partial<VaccineInputs>) => {
        set((state) => {
          const updatedGlobal = { ...state.globalInputs };
          const updatedRegional: RegionalState = {
            ...state.regional,
            coveragePctByRegion: { ...state.regional.coveragePctByRegion },
          };

          // Compute next inputs by merging current with patch (immutable)
          const currentInputs = state.inputs;
          const nextInputs = { ...currentInputs, ...patch };
          
          // Sanitize multi-year inputs when timeHorizonYears changes
          if (patch.timeHorizonYears !== undefined) {
            const newHorizon = patch.timeHorizonYears;
            
            if (newHorizon <= 1) {
              // Clear multi-year inputs when horizon ≤ 1
              nextInputs.boosterYears = [];
              nextInputs.boosterCost = 0;
              nextInputs.discountingEnabled = false;
              nextInputs.waningPctPerYear = 0; // Optional: avoid confusion
            } else {
              // Filter, deduplicate, and sort booster years
              const currentBoosterYears = currentInputs.boosterYears || [];
              const filtered = currentBoosterYears.filter(
                (y: number) => y >= 2 && y <= newHorizon
              );
              const unique = Array.from(new Set(filtered));
              nextInputs.boosterYears = unique.sort((a, b) => a - b);
              // Keep boosterCost and discountingEnabled as-is
            }
          }

          // Apply sanitized values to patch for processing
          const sanitizedPatch = { ...patch };
          if (patch.timeHorizonYears !== undefined) {
            // Always include sanitized multi-year values when horizon changes
            sanitizedPatch.boosterYears = nextInputs.boosterYears;
            sanitizedPatch.boosterCost = nextInputs.boosterCost;
            sanitizedPatch.discountingEnabled = nextInputs.discountingEnabled;
            sanitizedPatch.waningPctPerYear = nextInputs.waningPctPerYear;
          }

          Object.entries(sanitizedPatch).forEach(([key, value]) => {
            if (key === 'selectedRegions' && Array.isArray(value)) {
              updatedRegional.selectedRegions = value as string[];
              return;
            }
            if (key === 'coveragePctByRegion' && typeof value === 'object' && value !== null) {
              Object.entries(value as Record<string, number>).forEach(([regionKey, regionValue]) => {
                updatedRegional.coveragePctByRegion[regionKey] = regionValue;
              });
              return;
            }
            if (key === 'defaultCoveragePct' && typeof value === 'number') {
              updatedRegional.defaultCoveragePct = value;
              return;
            }
            if (key === 'population' || key === 'populationMode' || key === 'nationalCoveragePct') {
              return; // derived/invariant fields
            }
            if (key === 'vaccineName') {
               (updatedGlobal as any)[key] = value;
               return;
            }
            (updatedGlobal as any)[key] = value;
          });

          const normalizedRegional = normalizeRegionalSelection(updatedRegional);
          const inputs = deriveInputsFromState(updatedGlobal, normalizedRegional);

          // Track manual parameter changes (exclude system-generated patches)
          const updatedSources = { ...state.parameterSources };
          Object.keys(sanitizedPatch).forEach(key => {
            if (key !== 'selectedRegions' && key !== 'coveragePctByRegion' && 
                key !== 'population' && key !== 'populationMode' && key !== 'nationalCoveragePct') {
              // Check if this parameter already has a source and if it's being manually modified
              const existingSource = updatedSources[key];
              if (existingSource && existingSource.source === 'ai_extraction') {
                // Mark as user modified
                updatedSources[key] = {
                  ...existingSource,
                  userModified: true,
                  modificationDate: new Date().toISOString()
                };
              } else if (!existingSource) {
                // New manual entry
                updatedSources[key] = {
                  source: 'manual'
                };
              }
            }
          });

          return {
            globalInputs: updatedGlobal,
            regional: normalizedRegional,
            inputs,
            parameterSources: updatedSources
          };
        });
        debouncedSave(get());
      },
      
      setAudience: (value: string) => {
        set({ audience: value });
        debouncedSave(get());
        logActivity(`Changed audience to: ${AUDIENCES.find(a => a.key === value)?.label || value}`);
      },
      
      setDisease: (value: string) => {
        set({ disease: value });
        debouncedSave(get());
        logActivity(`Changed disease to: ${DISEASES.find(d => d.key === value)?.name || value}`);
      },
      
      setIsModelInputsCollapsed: (value: boolean) => {
        set({ isModelInputsCollapsed: value });
      },
      
      setActiveTab: (value: string) => {
        set({ activeTab: value });
        debouncedSave(get());
      },
      
      addScenario: (scenario: Scenario) => {
        set((state) => ({
          scenarios: [scenario, ...state.scenarios].slice(0, 10),
        }));
        debouncedSave(get());
        logActivity(`Saved scenario: ${scenario.name}`);
      },
      
      setBackendResults: (results: VaccineOutputs, computedAt: number, validation) => {
        set({ 
          backendResults: results, 
          backendComputedAt: computedAt,
          validationResults: validation || null,
        });
      },

      clearBackendResults: () => {
        set({ backendResults: null, backendComputedAt: null, validationResults: null });
        logActivity('Backend computation results cleared');
      },
      
      resetToDefaults: () => {
        const { globalInputs, regional } = convertInputsToState(DEFAULT_INPUTS);
        const normalizedRegional = normalizeRegionalSelection(regional);
        const inputs = deriveInputsFromState(globalInputs, normalizedRegional);
        set({ 
          inputs, 
          globalInputs, 
          regional: normalizedRegional,
          disease: '',
          audience: '',
          parameterSources: {} // Clear parameter sources on reset
        });
        debouncedSave(get());
        logActivity('Reset inputs to default values');
      },

      // Parameter source tracking actions
      trackParameterSource: (paramName: string, source: ParameterSource) => {
        set((state) => ({
          parameterSources: {
            ...state.parameterSources,
            [paramName]: source
          }
        }));
        debouncedSave(get());
        logActivity(`Tracked parameter source for ${paramName}: ${source.source}`);
      },

      getParameterSource: (paramName: string) => {
        return get().parameterSources[paramName];
      },
      

      
      handleCoverageChange: (region: string, value: number) => {
        let finalValue = 0;
        set((state) => {
          const cap = state.globalInputs.nationalCoveragePct;
          const validatedValue = clampValue(validateCoverage(value), 0, cap);
          finalValue = validatedValue;
          const updatedRegional: RegionalState = {
            ...state.regional,
            coveragePctByRegion: {
              ...state.regional.coveragePctByRegion,
              [region]: validatedValue,
            },
          };
          const normalizedRegional = normalizeRegionalSelection(updatedRegional);
          const inputs = deriveInputsFromState(state.globalInputs, normalizedRegional);
          return {
            regional: normalizedRegional,
            inputs,
          };
        });
        debouncedSave(get());
        const regionName = REGIONS.find(r => r.key === region)?.name || region;
        logActivity(`Updated coverage for ${regionName} to ${finalValue}%`);
      },
      
      handleRegionToggle: (regionKey: string) => {
        set((state) => {
          const currentRegions = state.regional.selectedRegions;
          const isSelected = currentRegions.includes(regionKey);
          
          let newRegions: string[];
          const newCoverageData = { ...state.regional.coveragePctByRegion };
          
          if (regionKey === 'finland') {
            newRegions = isSelected ? [] : ['finland'];
          } else {
            const regionsWithoutFinland = currentRegions.filter(r => r !== 'finland');
            if (isSelected) {
              newRegions = regionsWithoutFinland.filter(r => r !== regionKey);
              // Keep coverage data when deselected so region shows teal color
            } else {
              newRegions = [...regionsWithoutFinland, regionKey];
              if (!newCoverageData[regionKey]) {
                newCoverageData[regionKey] = state.regional.defaultCoveragePct;
              }
            }
          }
          
          const regionName = REGIONS.find(r => r.key === regionKey)?.name || regionKey;
          logActivity(`${isSelected ? 'Removed' : 'Added'} region: ${regionName}`);

          const updatedRegional = normalizeRegionalSelection({
            ...state.regional,
            selectedRegions: newRegions,
            coveragePctByRegion: newCoverageData,
          });
          const inputs = deriveInputsFromState(state.globalInputs, updatedRegional);
          
          return {
            regional: updatedRegional,
            inputs,
          };
        });
        debouncedSave(get());
      },
      
      handleRegionsChange: (regions: string[]) => {
        set((state) => {
          const normalized = normalizeSelectedRegions(regions);
          const newCoverageData = { ...state.regional.coveragePctByRegion };
          normalized.forEach(regionKey => {
            if (!newCoverageData[regionKey]) {
              newCoverageData[regionKey] = state.regional.defaultCoveragePct;
            }
          });

          const updatedRegional = normalizeRegionalSelection({
            ...state.regional,
            selectedRegions: normalized,
            coveragePctByRegion: newCoverageData,
          });
          const inputs = deriveInputsFromState(state.globalInputs, updatedRegional);

          return {
            regional: updatedRegional,
            inputs,
          };
        });
        debouncedSave(get());
      },

      setPopulationMode: (mode: PopulationMode) => {
        set((state) => {
          const updatedGlobal = {
            ...state.globalInputs,
            populationMode: mode,
            nationalPopulation: getNationalPopulation(mode),
          };
          const inputs = deriveInputsFromState(updatedGlobal, state.regional);
          return {
            globalInputs: updatedGlobal,
            inputs,
          };
        });
        debouncedSave(get());
      },

      setNationalCoveragePct: (value: number) => {
        set((state) => {
          const coverage = clampValue(value, 0, 100);
          const updatedGlobal = { ...state.globalInputs, nationalCoveragePct: coverage };
          const inputs = deriveInputsFromState(updatedGlobal, state.regional);
          return {
            globalInputs: updatedGlobal,
            inputs,
          };
        });
        debouncedSave(get());
        logActivity(`Updated national coverage to ${value}%`);
      },

      setNationalPopulation: (value: number) => {
        let population = 0;
        set((state) => {
          population = Math.max(0, value || 0);
          const updatedGlobal = { ...state.globalInputs, nationalPopulation: population };
          const inputs = deriveInputsFromState(updatedGlobal, state.regional);
          return {
            globalInputs: updatedGlobal,
            inputs,
          };
        });
        debouncedSave(get());
        logActivity(`Updated national population to ${population.toLocaleString('fi-FI')} people`);
      },

      setSelectedRegion: (regionId: string) => {
        set((state) => {
          if (!state.regional.selectedRegions.includes(regionId) && regionId !== 'finland') {
            return {};
          }
          const updatedRegional = normalizeRegionalSelection({
            ...state.regional,
            selectedRegionId: regionId,
          });
          return { regional: updatedRegional };
        });
      },
      
      setActivity: (activity: any[]) => {
        set({ activity });
        debouncedSave(get());
      },

      setDashboardSettings: (settings: Partial<DashboardSettings>) => {
        set((state) => ({
          dashboardSettings: {
            ...state.dashboardSettings,
            ...settings,
            visibleKPIs: {
              ...state.dashboardSettings.visibleKPIs,
              ...(settings.visibleKPIs || {}),
            },
          },
        }));
        debouncedSave(get());
      },

      loadScenario: (scenario: Scenario) => {
        console.log('[loadScenario] Loading scenario:', scenario.name);
        console.log('[loadScenario] Disease value from scenario:', scenario.disease);
        
        // Migration: Accept both snake_case and camelCase keys, normalize to camelCase
        const migratedInputs: any = { ...scenario.inputs };
        if (migratedInputs.boosterYears === undefined && migratedInputs.booster_years !== undefined) {
          migratedInputs.boosterYears = migratedInputs.booster_years;
          delete migratedInputs.booster_years;
        }
        if (migratedInputs.boosterCost === undefined && migratedInputs.booster_cost !== undefined) {
          migratedInputs.boosterCost = migratedInputs.booster_cost;
          delete migratedInputs.booster_cost;
        }
        
        const builtInputs = buildStateFromInputs(migratedInputs as VaccineInputs);
        const newState = {
          ...builtInputs,
          audience: scenario.audience,
          disease: scenario.disease,
          backendResults: scenario.outputs,
          backendComputedAt: scenario.timestamp,
          validationResults: null, // Scenarios don't store validation, will be recomputed
        };
        console.log('[loadScenario] Setting state with disease:', newState.disease);
        set(newState);
        // Verify the state was actually set
        const currentState = get();
        console.log('[loadScenario] State after set - disease:', currentState.disease);
        logActivity(`Loaded scenario: ${scenario.name}`);
      },

      deleteScenario: (id: string) => {
        set((state) => ({
          scenarios: state.scenarios.filter((s) => s.id !== id),
        }));
        debouncedSave(get());
        logActivity('Deleted scenario');
      },

      attachEvidenceToScenario: (scenarioId: string, pmid: string) => {
        set((state) => ({
          scenarios: state.scenarios.map((s) =>
            s.id === scenarioId
              ? {
                  ...s,
                  evidenceIds: [...new Set([...(s.evidenceIds || []), pmid])],
                }
              : s
          ),
        }));
        debouncedSave(get());
      },

      detachEvidenceFromScenario: (scenarioId: string, pmid: string) => {
        set((state) => ({
          scenarios: state.scenarios.map((s) =>
            s.id === scenarioId
              ? {
                  ...s,
                  evidenceIds: (s.evidenceIds || []).filter((id) => id !== pmid),
                }
              : s
          ),
        }));
        debouncedSave(get());
      },
    };
  })
);
